import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Plus, Loader2, Calendar, Clock, Link as LinkIcon, Save, X, Trash2, ExternalLink, Info,
} from "lucide-react";
import { api } from "../../lib/api";
import { formatReceivedAt } from "../../lib/dateFormat";

/**
 * V167 — Admin Schedule Panel.
 *
 * Renders inside AssignmentDetailModal → Sessions tab. Lets the admin
 * add / edit / cancel class_schedules with a per-session meeting URL
 * (Zoom / Google Meet / Teams / Jitsi / native 100ms).
 *
 * If a meeting_url is set, PortalPage.joinClass opens that URL in a new
 * tab. Otherwise it falls back to the native /live/{schedule_id} flow.
 */

const STATUS_OPTS = [
    { value: "proposed", label: "Proposed" },
    { value: "awaiting_confirmation", label: "Awaiting Confirmation" },
    { value: "confirmed", label: "Confirmed" },
    { value: "rescheduled", label: "Rescheduled" },
    { value: "completed", label: "Completed" },
    { value: "no_show", label: "No Show" },
    { value: "cancelled", label: "Cancelled" },
];

export default function SchedulePanel({ assignmentId, hmsEnabled = false }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [editing, setEditing] = useState(null);

    const reload = useCallback(async () => {
        try {
            const r = await api.get(`/admin/assignments/${assignmentId}/schedules`);
            setItems(r.data?.items || []);
        } catch (_e) { /* transient */ }
        finally { setLoading(false); }
    }, [assignmentId]);

    useEffect(() => { reload(); }, [reload]);

    const remove = async (s) => {
        if (!confirm(`Cancel Session #${s.session_number}? This won't delete history — it just marks the schedule as cancelled.`)) return;
        try {
            await api.patch(`/admin/assignments/${assignmentId}/schedules/${s.id}`, { status: "cancelled" });
            toast.success("Schedule cancelled");
            reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not cancel");
        }
    };

    const nextSessionNumber = () => {
        if (!items.length) return 1;
        const max = Math.max(...items.map((s) => s.session_number || 0));
        return max + 1;
    };

    return (
        <section data-testid="schedule-panel">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1.5">
                        <Calendar className="w-3 h-3" /> Sessions
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                        Schedule demo or regular classes. Paste a meeting URL (Zoom · Meet · Teams · Jitsi) and the portal Join button will open it directly — no 100ms setup required.
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => setShowCreate(true)}
                    data-testid="schedule-create-btn"
                    data-cm-primary
                    className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-semibold px-3 py-2 rounded-lg hover:bg-[#1E293B] shrink-0"
                >
                    <Plus className="w-4 h-4" /> Add class
                </button>
            </div>

            {!hmsEnabled && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-3 mb-3 text-xs text-cyan-900 flex items-start gap-2">
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                        <div className="font-semibold">Native 100ms video is not activated on this account.</div>
                        <div className="mt-0.5 text-cyan-800">
                            You can conduct classes right now using any external meeting link (Zoom, Google Meet, Microsoft Teams, or Jitsi). Just paste the URL below.
                            To enable in-platform video later, open Live Classroom Setup and follow the wizard.
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
                    <Loader2 className="w-4 h-4 mx-auto animate-spin text-slate-400" />
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-dashed border-slate-200 rounded-xl p-6 text-center text-xs text-slate-500">
                    No classes scheduled. Tap “Add class” to schedule the first demo.
                </div>
            ) : (
                <ul className="space-y-2" data-testid="schedule-list">
                    {items.map((s) => (
                        <li key={s.id}>
                            <ScheduleRow
                                schedule={s}
                                onEdit={() => setEditing(s)}
                                onCancel={() => remove(s)}
                            />
                        </li>
                    ))}
                </ul>
            )}

            {showCreate && (
                <ScheduleDialog
                    initial={{ session_number: nextSessionNumber() }}
                    assignmentId={assignmentId}
                    onClose={() => setShowCreate(false)}
                    onSaved={() => { setShowCreate(false); reload(); }}
                    hmsEnabled={hmsEnabled}
                />
            )}
            {editing && (
                <ScheduleDialog
                    initial={editing}
                    assignmentId={assignmentId}
                    isEdit
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); reload(); }}
                    hmsEnabled={hmsEnabled}
                />
            )}
        </section>
    );
}

function ScheduleRow({ schedule, onEdit, onCancel }) {
    const s = schedule;
    const external = (s.meeting_url || "").trim();
    const statusTone = {
        proposed: "bg-slate-100 text-slate-700 border-slate-200",
        awaiting_confirmation: "bg-amber-50 text-amber-800 border-amber-200",
        confirmed: "bg-cyan-50 text-cyan-800 border-cyan-200",
        rescheduled: "bg-amber-50 text-amber-800 border-amber-200",
        completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
        no_show: "bg-rose-50 text-rose-700 border-rose-200",
        cancelled: "bg-rose-50 text-rose-700 border-rose-200",
    }[s.status] || "bg-slate-100 text-slate-700 border-slate-200";

    return (
        <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`schedule-row-${s.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-heading font-bold text-[#0F172A]">
                        Session #{s.session_number}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatReceivedAt(s.scheduled_start)}
                        {s.scheduled_end && <> → {formatReceivedAt(s.scheduled_end)}</>}
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusTone}`}>
                            {STATUS_OPTS.find((o) => o.value === s.status)?.label || s.status}
                        </span>
                        {external ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-violet-50 text-violet-800 border border-violet-200 px-2 py-0.5 rounded-full">
                                <ExternalLink className="w-3 h-3" /> External link
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-slate-50 text-slate-700 border border-slate-200 px-2 py-0.5 rounded-full">
                                Native
                            </span>
                        )}
                    </div>
                    {external && (
                        <a
                            href={external}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[11px] text-cyan-700 hover:underline truncate block mt-1"
                            data-testid={`schedule-url-${s.id}`}
                        >
                            {external}
                        </a>
                    )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <button
                        type="button"
                        onClick={onEdit}
                        data-testid={`schedule-edit-${s.id}`}
                        data-cm-compact
                        aria-label="Edit"
                        className="p-2 text-slate-600 hover:text-cyan-700 hover:bg-slate-50 rounded"
                    >
                        <LinkIcon className="w-3.5 h-3.5" />
                    </button>
                    {s.status !== "cancelled" && s.status !== "completed" && (
                        <button
                            type="button"
                            onClick={onCancel}
                            data-testid={`schedule-cancel-${s.id}`}
                            data-cm-compact
                            aria-label="Cancel"
                            className="p-2 text-rose-600 hover:bg-rose-50 rounded"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function ScheduleDialog({ initial, assignmentId, isEdit, onClose, onSaved, hmsEnabled }) {
    const [sessionNumber, setSessionNumber] = useState(initial.session_number || 1);
    const [startLocal, setStartLocal] = useState(toLocalInput(initial.scheduled_start));
    const [durationMin, setDurationMin] = useState(deriveDuration(initial));
    const [status, setStatus] = useState(initial.status || "confirmed");
    const [meetingUrl, setMeetingUrl] = useState(initial.meeting_url || "");
    const [tutorNote, setTutorNote] = useState(initial.tutor_note || "");
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!startLocal) { toast.error("Please pick a date & time"); return; }
        if (durationMin <= 0) { toast.error("Duration must be positive"); return; }
        const startIso = new Date(startLocal).toISOString();
        const endIso = new Date(new Date(startLocal).getTime() + durationMin * 60000).toISOString();
        const url = (meetingUrl || "").trim();
        if (url && !/^https?:\/\//i.test(url)) {
            toast.error("Meeting URL must start with http:// or https://");
            return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                await api.patch(`/admin/assignments/${assignmentId}/schedules/${initial.id}`, {
                    scheduled_start: startIso,
                    scheduled_end: endIso,
                    status,
                    meeting_url: url,
                    tutor_note: tutorNote.trim() || null,
                });
                toast.success("Schedule updated");
            } else {
                await api.post(`/admin/assignments/${assignmentId}/schedules`, {
                    session_number: Number(sessionNumber),
                    scheduled_start: startIso,
                    scheduled_end: endIso,
                    meeting_url: url,
                    tutor_note: tutorNote.trim() || null,
                });
                toast.success("Class scheduled");
            }
            onSaved?.();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : (detail?.message || "Could not save schedule");
            toast.error(msg);
        } finally { setSaving(false); }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 z-[95] flex items-start sm:items-center justify-center overflow-y-auto p-0 sm:p-4 classroom-mobile"
            data-testid="schedule-dialog"
        >
            <form onSubmit={submit} className="bg-white w-full sm:max-w-md sm:rounded-2xl shadow-2xl min-h-screen sm:min-h-0 sm:max-h-[92vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-5 py-3 flex items-center justify-between z-10">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-cyan-600 font-bold">
                            {isEdit ? "Edit Class" : "Schedule Class"}
                        </div>
                        <div className="text-lg font-heading font-bold text-[#0F172A]">
                            {isEdit ? `Session #${initial.session_number}` : "New session"}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        data-testid="schedule-dialog-close"
                        data-cm-compact
                        aria-label="Close"
                        className="p-2 hover:bg-slate-100 rounded-lg text-slate-600 disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-4 sm:p-5 space-y-4">
                    {!isEdit && (
                        <F label="Session number">
                            <input
                                type="number" min={1} max={999}
                                value={sessionNumber}
                                onChange={(e) => setSessionNumber(e.target.value)}
                                data-testid="schedule-session-number"
                                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                            />
                        </F>
                    )}
                    <F label="Start date & time *">
                        <input
                            type="datetime-local"
                            value={startLocal}
                            onChange={(e) => setStartLocal(e.target.value)}
                            data-testid="schedule-start"
                            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                        />
                    </F>
                    <F label="Duration (minutes)">
                        <input
                            type="number" min={5} max={600} step={5}
                            value={durationMin}
                            onChange={(e) => setDurationMin(Number(e.target.value))}
                            data-testid="schedule-duration"
                            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                        />
                    </F>
                    <F label={`Meeting URL${hmsEnabled ? " (optional — leave empty to use native 100ms video)" : " (Zoom · Meet · Teams · Jitsi)"}`}>
                        <input
                            type="url"
                            value={meetingUrl}
                            onChange={(e) => setMeetingUrl(e.target.value)}
                            data-testid="schedule-meeting-url"
                            placeholder="https://meet.google.com/abc-defg-hij"
                            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                        />
                        <div className="text-[10px] text-slate-500 mt-1">
                            {meetingUrl.trim()
                                ? "Portal Join Class buttons will open this URL in a new tab."
                                : hmsEnabled
                                    ? "Empty → Portal Join Class opens the native 100ms classroom."
                                    : "Paste any meeting URL — required unless native 100ms is set up."}
                        </div>
                    </F>
                    <F label="Note for tutor / student (optional)">
                        <textarea
                            rows={2}
                            maxLength={400}
                            value={tutorNote}
                            onChange={(e) => setTutorNote(e.target.value)}
                            data-testid="schedule-tutor-note"
                            placeholder="e.g., Please cover Ch 3 exercises 1–5"
                            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none resize-none"
                        />
                    </F>
                    {isEdit && (
                        <F label="Status">
                            <select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                data-testid="schedule-status"
                                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 outline-none"
                            >
                                {STATUS_OPTS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </F>
                    )}
                </div>
                <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 sm:px-5 py-3 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid="schedule-save"
                        data-cm-primary
                        className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#1E293B] disabled:opacity-60"
                    >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {isEdit ? "Save" : "Create"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function F({ label, children }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">{label}</div>
            {children}
        </label>
    );
}

function toLocalInput(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        // to yyyy-MM-ddTHH:mm in the browser's local zone
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ""; }
}

function deriveDuration(sch) {
    try {
        if (!sch?.scheduled_start || !sch?.scheduled_end) return 60;
        const s = new Date(sch.scheduled_start).getTime();
        const e = new Date(sch.scheduled_end).getTime();
        return Math.max(5, Math.round((e - s) / 60000));
    } catch { return 60; }
}
